import type { PagedResult } from '@/shared/types/pagedResult';

export const FIELD_TYPE = {
  Text: 0,
  Number: 1,
  Date: 2,
  Select: 3,
  MultiSelect: 4,
  Checkbox: 5,
  Users: 6,
  Email: 7,
  Url: 8,
  Phone: 9,
  Currency: 10,
  Files: 11,
  Signature: 12,
} as const;

export type FieldType = (typeof FIELD_TYPE)[keyof typeof FIELD_TYPE];

export interface ProjectFieldOption {
  id: string | null;
  name: string | null;
  value: string | null;
  color: string | null;
  orderIndex: number;
}

/**
 * A custom field definition scoped to a project.
 *
 * @remarks
 * - `fieldType` is a string from the API (e.g. "Text", "Number") — use `resolveFieldType` to convert to `FieldType` number.
 * - `typeConfig` is a JSON-serialized string containing per-type settings and permissions. Parse with `JSON.parse` before reading.
 * - `defaultValue` is always a string regardless of field type (e.g. checkbox stores `"true"` or `"false"`).
 * - `options` is only populated for Select and MultiSelect field types.
 */
export interface ProjectField {
  id: string | null;
  projectId: string | null;
  name: string | null;
  fieldType: string | null;
  required: boolean;
  typeConfig: string | null;
  description: string | null;
  defaultValue: string | null;
  options: ProjectFieldOption[] | null;
  order: number;
  createdAt: string;
}

export type ProjectFieldPagedResult = PagedResult<ProjectField>;

export interface ProjectFieldFilters {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortAscending?: boolean;
}

export interface FieldValue {
  id: string | null;
  taskId: string | null;
  fieldId: string | null;
  value: string | null;
  updatedAt: string | null;
}

/**
 * Parsed shape of `FieldValue.value` for `Files` and `Signature` field types.
 *
 * @remarks
 * The backend stores this as a JSON string inside `FieldValue.value` — parse it with
 * `parseFieldValueFile` before use. `signedByUserId`/`signedAt` are only present for `Signature`.
 */
export interface FieldValueFile {
  storageKey: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  signedByUserId?: string | null;
  signedAt?: string | null;
}
